import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { StandardItem, StandardPlace } from '../../types/standard';
import { standardClient } from '../../lib/standard';
import { useHeader } from '../../context/HeaderContext';
import { CategoryFilter } from '../shared/CategoryFilter';
import { ListView } from './ListView';
import MapView from './MapView';
import { ViewToggle } from './ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { useAnalytics } from '../../hooks/useAnalytics';
import ErrorBoundary from '../analytics/ErrorBoundary';

const StandardSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'standard'
  });
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedItem, setSelectedItem] = useState<StandardItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<StandardPlace | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch items using section.id
  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ['standard-items', section?.id],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }
      
      try {
        const result = await standardClient.getItems(section.id);
        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!section?.id,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analytics.trackSectionView(view);
    }
  }, [section?.id, view, analytics]);

  // Category colors memo
  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, items]);

  // Filtered items memo
  const filteredItems = React.useMemo(() => {
    if (!selectedCategory && !selectedType) return items;
    return items.filter(item => {
      const categoryMatch = !selectedCategory || item.category === selectedCategory;
      const typeMatch = !selectedType || item.type === selectedType;
      return categoryMatch && typeMatch;
    });
  }, [items, selectedCategory, selectedType]);

  // Places memo
  const places = React.useMemo(() => {
    return standardClient.getPlaces(filteredItems);
  }, [filteredItems]);

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items) {
      const item = items.find(i => slugify(i.name) === name);
      if (item) {
        setSelectedItem(item);
        
        // Track detail card view from URL navigation
        analytics.trackItemAction(item.id, 'item_click', 'standard', {
          item_name: item.name,
          category: item.category,
          type: item.type,
          source: 'url_navigation'
        });
      }
    }
  }, [name, items, analytics]);

  // Event handlers
  const handleBackClick = () => {
    analytics.trackEvent('navigation', 'menu_click', {
      target: 'home',
      section_id: section?.id,
      section_name: section?.names?.fr
    });
    
    navigate('/');
  };

  const handleCategorySelect = (category: string) => {
    analytics.trackFilter('category_filter', category);
    
    setSelectedCategory(category);
    setSelectedType('');
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleTypeSelect = (type: string) => {
    analytics.trackFilter('type_filter', type);
    
    const itemWithType = items.find(item => item.type === type);
    if (itemWithType) {
      setSelectedCategory(itemWithType.category);
    }
    setSelectedType(type);
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      section_id: section?.id,
      section_name: section?.names?.fr,
      section_type: 'standard',
      view_type: newView,
      previous_view: view
    });
    
    setView(newView);
  };

  const handleItemClick = (item: StandardItem) => {
    // Save current scroll position before navigating
    scrollPositionRef.current = window.scrollY;
    
    analytics.trackItemAction(item.id, 'item_click', 'standard', {
      item_name: item.name,
      category: item.category,
      type: item.type,
      source: view === 'list' ? 'list_view' : 'map_view'
    });
    
    const itemSlug = slugify(item.name);
    
    // Use relative navigation
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}/${itemSlug}`);
    
    setSelectedItem(item);
    setDetailBehavior('default');
    setSelectedPlace(null);
  };

  const handlePlaceClick = (place: StandardPlace) => {
    analytics.trackEvent('interaction', 'place_card_view', {
      section_id: section?.id,
      place_name: place.name,
      items_count: place.items.length
    });
    
    setSelectedPlace(place);
    setSelectedItem(null);
  };

  const handleEventFromPlace = (event: StandardItem, showIndex: number) => {
    if (!section?.names?.fr) return;

    analytics.trackItemAction(event.id, 'item_click', 'standard', {
      item_name: event.name,
      category: event.category,
      type: event.type,
      source: 'place_card',
      place_name: selectedPlace?.name,
      show_index: showIndex
    });
    
    const sectionSlug = slugify(section.names.fr);
    const itemSlug = slugify(event.name);
    navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
    
    setSelectedItem(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
  };

  const handleItemChange = (newItem: StandardItem) => {
    if (!section?.names?.fr) return;

    analytics.trackItemAction(newItem.id, 'navigation_next', 'standard', {
      item_name: newItem.name,
      category: newItem.category,
      type: newItem.type,
      previous_item_id: selectedItem?.id,
      previous_item_name: selectedItem?.name
    });
    
    const sectionSlug = slugify(section.names.fr);
    const itemSlug = slugify(newItem.name);
    
    // Use relative navigation
    navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
    
    if (detailBehavior === 'place' && selectedPlace) {
      // Find the new index in the shows
      const newIndex = selectedPlace.items.findIndex(item => item.id === newItem.id);
      if (newIndex !== -1) {
        setSelectedItem(newItem);
        setSelectedShowIndex(newIndex);
      }
    } else {
      setSelectedItem(newItem);
    }
  };

  const handleDetailClose = () => {
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`, { replace: true });
    
    analytics.trackEvent('interaction', 'detail_card_close', {
      section_id: section?.id,
      item_id: selectedItem?.id,
      item_name: selectedItem?.name,
      view_duration: selectedItem ? Date.now() - scrollPositionRef.current : 0
    });
    
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 50);
  };

  // Loading state
  if (configLoading || translationsLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (itemsError || !config || !translations || !section) {
    analytics.trackError('section_load_error', itemsError || new Error('Missing configuration'), {
      section_id: section?.id,
      section_type: 'standard'
    });
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary 
      componentName="StandardSection" 
      sectionId={section.id}
      sectionName={section.names.fr}
    >
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
        }}
      >
        <SectionHeader
          config={config}
          title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />

        {/* Filter */}
        <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
          <CategoryFilter
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            onCategoryChange={handleCategorySelect}
            onTypeChange={handleTypeSelect}
            config={config}
            translations={translations}
            items={items}
          />
        </div>

        {/* Main content */}
        <main className="main-content">
          {itemsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingIndicator />
            </div>
          ) : view === 'list' ? (
            <ListView
              items={filteredItems}
              onItemClick={handleItemClick}
              onCategorySelect={handleCategorySelect}
              onTypeSelect={handleTypeSelect}
              config={config}
              translations={translations}
              categoryColors={categoryColors}
            />
          ) : (
            <div 
              className="fixed inset-0 pt-[94px] pb-[40px]"
              style={{ zIndex: 1 }}
            >
              <MapView
                places={places}
                config={config}
                translations={translations}
                onItemClick={handleItemClick}
                onPlaceClick={handlePlaceClick}
                mainLocation={{
                  name: config.name,
                  latitude: config.latitude,
                  longitude: config.longitude,
                  fulladress: config.fulladress
                }}
                categoryColors={categoryColors}
                onCategorySelect={handleCategorySelect}
                onTypeSelect={handleTypeSelect}
                mapInstanceRef={mapInstanceRef}
              />
            </div>
          )}
        </main>

        {/* Fixed view toggle button */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
        >
          <ViewToggle
            view={view}
            onChange={handleViewChange}
            config={config}
            translations={translations}
          />
        </div>

        {/* Modals */}
        {selectedPlace && !selectedItem && (
          <PlaceCard
            place={selectedPlace}
            onClose={() => {
              analytics.trackEvent('interaction', 'place_card_close', {
                place_name: selectedPlace.name,
                items_count: selectedPlace.items.length
              });
              setSelectedPlace(null);
            }}
            onEventClick={(item) => {
              const itemSlug = slugify(item.name);
              const sectionSlug = slugify(section.names.fr);
              navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
              setSelectedItem(item);
              setSelectedPlace(null);
            }}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
            selectedDate=""
          />
        )}

        {selectedItem && (
          <DetailCard
            item={selectedItem}
            items={filteredItems}
            onClose={handleDetailClose}
            onItemChange={handleItemChange}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            tableId={section.id}
            categoryColors={categoryColors}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default StandardSection;
export { StandardSection };